/**
 * The UI-stage endpoint deliberately returns `200` + `null` when no cached
 * snapshot can be built (for example, Stage 1 has saved input but no output).
 * Treat that response as a cache miss, not as restore data.
 */
export type RestorableStageData = {
  input: Record<string, unknown> | null;
  output: Record<string, unknown> | null;
};

export type RestoredAnswers = Record<string, string>;

type JsonResponse = {
  ok: boolean;
  json: () => Promise<unknown>;
};

type FetchStageResponse = () => Promise<JsonResponse>;

const isRecord = (value: unknown): value is Record<string, unknown> => (
  value !== null && typeof value === 'object' && !Array.isArray(value)
);

function answerMapFromValue(value: unknown): RestoredAnswers {
  if (isRecord(value)) {
    return Object.entries(value).reduce<RestoredAnswers>((answers, [questionId, answer]) => {
      if (typeof answer === 'string') {
        answers[questionId] = answer;
      } else if (isRecord(answer) && typeof answer.answer === 'string') {
        answers[questionId] = answer.answer;
      }
      return answers;
    }, {});
  }

  if (!Array.isArray(value)) return {};

  return value.reduce<RestoredAnswers>((answers, item) => {
    if (!isRecord(item)) return answers;
    const question = isRecord(item.question) ? item.question : null;
    const questionId = item.questionId ?? item.question_id ?? item.id
      ?? question?.questionId ?? question?.question_id ?? question?.id;
    const answer = item.answer ?? question?.answer;
    if (typeof questionId === 'string' && typeof answer === 'string') {
      answers[questionId] = answer;
    }
    return answers;
  }, {});
}

/**
 * Reconstruct the canonical ID-keyed answer map for a restored MR stage.
 *
 * Raw Stage 2 output from older flows stores answers as a positional string
 * array, which has no question IDs and must never overwrite Stage 1 answers.
 * Its persisted `questions_and_answers` input retains both IDs and values, so
 * prefer that durable representation whenever it is available.
 */
export function restoredAnswersForStage(
  stageNum: number,
  stageData: RestorableStageData | null,
): RestoredAnswers {
  if (!stageData) return {};

  if (stageNum === 2) {
    const questionAndAnswers = stageData.input?.questions_and_answers
      ?? stageData.input?.questionsAndAnswers;
    const answersFromInput = answerMapFromValue(questionAndAnswers);
    if (Object.keys(answersFromInput).length > 0) return answersFromInput;
  }

  return answerMapFromValue(stageData.output?.answers);
}

async function readStagePayload(
  fetchResponse: FetchStageResponse,
  source: 'UI cache' | 'raw stage',
): Promise<RestorableStageData | null> {
  let response: JsonResponse;
  try {
    response = await fetchResponse();
  } catch (error) {
    console.warn(`[App] Unable to fetch ${source} while restoring a stage`, error);
    return null;
  }

  if (!response.ok) return null;

  let payload: unknown;
  try {
    payload = await response.json();
  } catch (error) {
    console.warn(`[App] Unable to parse ${source} while restoring a stage`, error);
    return null;
  }

  if (!isRecord(payload)) {
    if (source === 'raw stage') {
      console.warn('[App] Skipping stage restore because the raw stage payload was not an object');
    }
    return null;
  }

  const input = isRecord(payload.input) ? payload.input : null;
  const output = isRecord(payload.output) ? payload.output : null;
  return { input, output };
}

/**
 * Load a UI snapshot when present, otherwise hydrate from the raw stage API.
 * Invalid/missing stage payloads are intentionally skipped so a stale cache
 * for one completed stage cannot prevent the rest of a workflow from opening.
 */
export async function loadRestorableStageData(
  fetchUi: FetchStageResponse,
  fetchRaw: FetchStageResponse,
): Promise<{ data: RestorableStageData | null; isUIFormat: boolean }> {
  const uiData = await readStagePayload(fetchUi, 'UI cache');
  if (uiData) return { data: uiData, isUIFormat: true };

  const rawData = await readStagePayload(fetchRaw, 'raw stage');
  return { data: rawData, isUIFormat: false };
}
